// Страница симулятора. Всё, что показано, СЧИТАЕТСЯ здесь и сейчас —
// ни одна кривая не нарисована заранее. В этом весь смысл: «край хаоса» должен
// проявиться из замера при движении ползунка, а не стоять картинкой.

import { DelayLineReservoir, memoryCapacity, sweepGain } from './reservoir.js';

const $ = (id) => document.getElementById(id);
const T = window.__RS_I18N || {};

const state = {
    nodes: 80,
    gain: 0.9,
    leak: 0.6,
    inputScale: 0.6,
    seed: 1,
    sweep: null,
    sweepPeak: null,
};

function reservoir() {
    return new DelayLineReservoir({
        nodes: state.nodes, gain: state.gain,
        leak: state.leak, inputScale: state.inputScale, seed: state.seed,
    });
}

// ── Отрисовка состояний: время слева направо, узлы сверху вниз ────────
function drawStates(canvas, states) {
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const cols = Math.min(states.length, 220);
    const start = states.length - cols;
    const rows = states[0].length;
    const cw = w / cols, ch = h / rows;
    for (let c = 0; c < cols; c++) {
        const s = states[start + c];
        for (let r = 0; r < rows; r++) {
            const v = Math.max(-1, Math.min(1, s[r]));
            // Расходящаяся шкала: отрицательные холодные, положительные тёплые,
            // ноль — фон. Знак состояния здесь несёт смысл, и терять его нельзя.
            const t = Math.abs(v);
            ctx.fillStyle = v >= 0
                ? `rgba(96,165,250,${0.08 + t * 0.9})`
                : `rgba(248,113,113,${0.08 + t * 0.9})`;
            ctx.fillRect(c * cw, r * ch, Math.ceil(cw), Math.ceil(ch));
        }
    }
}

// ── Столбики памяти по задержкам ──────────────────────────────────────
function drawMemory(canvas, perDelay) {
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const css = getComputedStyle(document.documentElement);
    const accent = css.getPropertyValue('--accent').trim() || '#3b82f6';
    const muted = css.getPropertyValue('--text-muted').trim() || '#94a3b8';
    const pad = 22;
    const n = perDelay.length;
    const bw = (w - pad) / n;
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = muted;
    ctx.fillText('1', pad, h - 3);
    ctx.fillText(String(n), w - 14, h - 3);
    for (let i = 0; i < n; i++) {
        const bh = (h - 18) * perDelay[i];
        ctx.fillStyle = accent;
        ctx.globalAlpha = 0.35 + 0.65 * perDelay[i];
        ctx.fillRect(pad + i * bw + 1, h - 14 - bh, Math.max(1, bw - 2), bh);
    }
    ctx.globalAlpha = 1;
}

// ── Кривая «память от усиления петли» ─────────────────────────────────
function drawSweep(canvas, sweep, current) {
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (!sweep || !sweep.length) return;

    const css = getComputedStyle(document.documentElement);
    const accent = css.getPropertyValue('--accent').trim() || '#3b82f6';
    const border = css.getPropertyValue('--border').trim() || '#334155';
    const maxV = Math.max(...sweep.map((p) => p.total)) || 1;
    const minG = sweep[0].gain, maxG = sweep[sweep.length - 1].gain;
    const x = (g) => 6 + ((g - minG) / (maxG - minG)) * (w - 12);
    const y = (v) => h - 16 - (v / maxV) * (h - 26);

    ctx.strokeStyle = border;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(6, h - 16); ctx.lineTo(w - 6, h - 16); ctx.stroke();

    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    sweep.forEach((p, i) => { const px = x(p.gain), py = y(p.total); i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
    ctx.stroke();

    // Метка текущего положения ползунка — связь между «я кручу» и «вот где я».
    const cx = x(current);
    ctx.strokeStyle = accent;
    ctx.globalAlpha = 0.45;
    ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(cx, 6); ctx.lineTo(cx, h - 16); ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
}

// ── Пересчёт ──────────────────────────────────────────────────────────
let pending = null;
function recompute() {
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => {
        pending = null;
        const res = reservoir();
        const mc = memoryCapacity(res, { steps: 1100, maxDelay: 20, washout: 100 });

        drawStates($('rs-states'), mc.states);
        drawMemory($('rs-memory'), mc.perDelay);
        drawSweep($('rs-sweep'), state.sweep, state.gain);

        $('rs-mc').textContent = mc.total.toFixed(2);
        const peak = state.sweepPeak ? state.sweepPeak.total : mc.total;
        const share = peak > 0 ? mc.total / peak : 0;

        let key;
        if (share > 0.9) key = 'edge';
        else if (state.gain < (state.sweepPeak ? state.sweepPeak.gain : 1)) key = 'fading';
        else key = 'chaotic';
        $('rs-regime').textContent = T['regime_' + key] || key;
        $('rs-regime').dataset.regime = key;
        $('rs-regime-note').textContent = T['regime_' + key + '_note'] || '';

        if (window.__stage) window.__stage('recompute');
    });
}

function runSweep() {
    const btn = $('rs-sweep-btn');
    if (btn) { btn.disabled = true; btn.textContent = T.sweep_running || '…'; }
    // Через кадр, чтобы кнопка успела перерисоваться до счёта.
    requestAnimationFrame(() => {
        setTimeout(() => {
            state.sweep = sweepGain(state, { points: 22, steps: 700, maxDelay: 20 });
            state.sweepPeak = state.sweep.reduce((a, b) => (b.total > a.total ? b : a));
            if ($('rs-peak')) $('rs-peak').textContent = state.sweepPeak.gain.toFixed(2);
            if (btn) { btn.disabled = false; btn.textContent = T.sweep_btn || 'Sweep'; }
            recompute();
            if (window.__aeFunnel) window.__aeFunnel('sweep_done', { peak: state.sweepPeak.gain });
        }, 16);
    });
}

function bindSlider(id, key, fmt) {
    const el = $(id);
    if (!el) return;
    const out = $(id + '-val');
    const apply = () => {
        state[key] = parseFloat(el.value);
        if (out) out.textContent = fmt ? fmt(state[key]) : state[key];
        recompute();
    };
    el.addEventListener('input', apply);
    if (window.__toolSettings) window.__toolSettings.bind('reservoir-computing', el, key, el.value);
    apply();
}

function init() {
    if (!$('rs-states')) return;
    bindSlider('rs-gain', 'gain', (v) => v.toFixed(2));
    bindSlider('rs-leak', 'leak', (v) => v.toFixed(2));
    bindSlider('rs-input', 'inputScale', (v) => v.toFixed(2));
    bindSlider('rs-nodes', 'nodes', (v) => String(Math.round(v)));

    const btn = $('rs-sweep-btn');
    if (btn) btn.addEventListener('click', runSweep);

    const reseed = $('rs-reseed');
    if (reseed) reseed.addEventListener('click', () => {
        // Новый случайный резервуар. Смысл кнопки — показать, что качество
        // держится на СХЕМЕ, а не на удачной реализации случайных связей.
        state.seed = (state.seed * 7919 + 13) % 100000;
        state.sweep = null; state.sweepPeak = null;
        recompute();
    });

    window.addEventListener('resize', () => recompute());
    recompute();
    // Свип считается сразу: без кривой пик неоткуда узнать, а ждать нажатия
    // означало бы, что главное в туле пользователь может не увидеть вовсе.
    runSweep();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
